import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BookingState } from '../types/booking-state';
import { Room } from '../types/room';
import { BookingDetails } from '../types/booking-details';
import { Building } from '../types/building';
import { DevelopmentSlug } from '../types/enums/DevelopmentSlug';

const initialState: BookingState = {
    id: null,
    room: null,
    details: null,
    error: null,
    building: null,
    bookingRecordId: null,
};

const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        setRoomData: (state: BookingState, action: PayloadAction<Room>) => {
            state.room = action.payload;
        },
        setBookingDetails: (state: BookingState, action: PayloadAction<BookingDetails>) => {
            state.details = action.payload;
        },
        setBuildingData: (state: BookingState, action: PayloadAction<Building>) => {
            state.building = action.payload;
        },
        setBookingError: (state: BookingState, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
        clearBooking: (state: BookingState) => {
            state.id = null;
            state.room = null;
            state.details = null;
            state.error = null;
        },
        setBookingRecordId: (state: BookingState, action: PayloadAction<string>) => {
            state.bookingRecordId = action.payload;
        },
    },
});

export const {
    setRoomData,
    setBookingDetails,
    setBookingError,
    clearBooking,
    setBuildingData,
    setBookingRecordId,
} = bookingSlice.actions;

export default bookingSlice.reducer;

const selectBookingState = (state: { booking: BookingState }) => state.booking;

export const selectRoom = createSelector([selectBookingState], (booking) => booking.room);

export const selectBuilding = createSelector([selectBookingState], (booking) => booking.building);

export const selectIsHouseOfSocial = createSelector([selectBookingState], (booking) => {
    if (!booking.building) return false;

    return booking.building.development_slug === DevelopmentSlug.HOUSE_OF_SOCIAL;
});

export const selectBookingDetails = createSelector(
    [selectBookingState],
    (booking) => booking.details
);

export const selectBookingError = createSelector([selectBookingState], (booking) => booking.error);

export const selectBookingRecordId = (state: { booking: BookingState }) =>
    state.booking.bookingRecordId;
